import { RuntimeFormError, type GoogleReCaptcha } from './types';

const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
const CONTAINER_ID = 'vibe-form-captcha';
/** Backstop for a callback that never fires; long enough not to cut off a visitor solving a challenge. */
const CHALLENGE_TIMEOUT_MS = 120_000;

interface Deferred<T> {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (error: unknown) => void;
}

let scriptPromise: Promise<void> | null = null;
let widgetPromise: Promise<number> | null = null;
let pendingToken: Deferred<string> | null = null;
let tokenRequest: Promise<string> | null = null;

function deferred<T>(): Deferred<T> {
    let resolve!: (value: T) => void;
    let reject!: (error: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return { promise, resolve, reject };
}

function captchaFailed(message: string): RuntimeFormError {
    return new RuntimeFormError(0, 'CAPTCHA_FAILED', message);
}

function grecaptcha(): GoogleReCaptcha {
    if (!window.grecaptcha) {
        throw captchaFailed('reCAPTCHA is unavailable');
    }
    return window.grecaptcha;
}

/**
 * Appends our own tag even when one is already on the page: a tag that already settled with an error
 * never re-fires load or error, so attaching to it would wait forever.
 */
function loadScript(): Promise<void> {
    if (window.grecaptcha) {
        return Promise.resolve();
    }
    if (scriptPromise) {
        return scriptPromise;
    }
    scriptPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.async = true;
        script.onload = () => {
            if (window.grecaptcha?.ready) {
                window.grecaptcha.ready(() => resolve());
            } else {
                resolve();
            }
        };
        script.onerror = () => {
            scriptPromise = null;
            reject(captchaFailed('Failed to load reCAPTCHA'));
        };
        document.head.appendChild(script);
    });
    return scriptPromise;
}

/** Left unstyled on purpose: hiding the badge would break reCAPTCHA's attribution requirement. */
function container(): HTMLElement {
    const existing = document.getElementById(CONTAINER_ID);
    if (existing) {
        return existing;
    }
    const created = document.createElement('div');
    created.id = CONTAINER_ID;
    document.body.appendChild(created);
    return created;
}

function ensureWidget(siteKey: string): Promise<number> {
    if (widgetPromise) {
        return widgetPromise;
    }
    widgetPromise = (async () => {
        await loadScript();
        // Rendering our own widget keeps us independent of any other captcha sharing window.grecaptcha.
        return grecaptcha().render(container(), {
            sitekey: siteKey,
            size: 'invisible',
            // render() takes the callback once, so every later execute() resolves through it.
            callback: (token: string) => pendingToken?.resolve(token),
            'error-callback': () =>
                pendingToken?.reject(
                    captchaFailed('reCAPTCHA could not issue a token'),
                ),
        });
    })();
    widgetPromise.catch(() => {
        widgetPromise = null;
    });
    return widgetPromise;
}

/**
 * Dismissing a challenge fires no callback and has no event, so watch the challenge frame fade out —
 * the same signal the monolith's runtime uses.
 */
function observeDismissal(onDismissed: () => void): MutationObserver | null {
    const frame = [...document.getElementsByTagName('iframe')].find((iframe) =>
        iframe.src?.includes('google.com/recaptcha/api2/bframe'),
    )?.parentNode?.parentNode;
    if (!frame) {
        return null;
    }
    const observer = new MutationObserver((mutations) => {
        const closed = mutations.some(
            (mutation) =>
                mutation.type === 'attributes' &&
                (mutation.target as HTMLElement).style?.opacity === '0',
        );
        if (closed) {
            onDismissed();
        }
    });
    observer.observe(frame as Node, {
        attributes: true,
        attributeFilter: ['style'],
    });
    return observer;
}

async function requestToken(siteKey: string): Promise<string> {
    const widgetId = await ensureWidget(siteKey);
    const pending = deferred<string>();
    pendingToken = pending;
    let observer: MutationObserver | null = null;
    const timeout = setTimeout(
        () => pending.reject(captchaFailed('reCAPTCHA timed out')),
        CHALLENGE_TIMEOUT_MS,
    );
    try {
        grecaptcha().execute(widgetId);
        observer = observeDismissal(() =>
            pending.reject(captchaFailed('reCAPTCHA was dismissed')),
        );
        return await pending.promise;
    } finally {
        clearTimeout(timeout);
        observer?.disconnect();
        pendingToken = null;
        // Tokens are single-use, so without a reset the next submission replays one the server rejects.
        window.grecaptcha?.reset(widgetId);
    }
}

/**
 * An invisible widget supports a single execute(), so concurrent submissions share the in-flight
 * request instead of orphaning each other's callback.
 */
export function mintCaptchaToken(siteKey: string): Promise<string> {
    if (tokenRequest) {
        return tokenRequest;
    }
    tokenRequest = requestToken(siteKey).finally(() => {
        tokenRequest = null;
    });
    return tokenRequest;
}
